import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ClipboardCheck,
  DollarSign,
  Eye,
  Image as ImageIcon,
  Layers3,
  ListChecks,
  PackageCheck,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Store,
  Trash2,
  UploadCloud,
  Tag,
  X,
  XCircle,
} from "lucide-react";
import { restaurantAdminService, rowsOf, unwrap } from "../../services/restaurantAdmin.service";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Input,
  LoadingBox,
  PageHeader,
  Select,
  StatusPill,
  TableShell,
  Textarea,
  idOf,
} from "./components/FoodAdminUi";

const CATALOG_BASE_URL =
  process.env.REACT_APP_CATALOG_BASE_URL ||
  process.env.REACT_APP_CATALOG_SERVICE_URL ||
  "http://localhost:6006";

const RESTAURANT_APP_BASE_URL =
  process.env.REACT_APP_RESTAURANT_APP_BASE_URL ||
  process.env.REACT_APP_RESTAURANT_SERVICE_URL ||
  "http://localhost:6040";

function resolveFoodImageUrl(url) {
  if (!url) return "";
  const value = String(url).trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:image")) return value;
  if (value.startsWith("/uploads")) return `${String(CATALOG_BASE_URL).replace(/\/$/, "")}${value}`;
  return value;
}

function resolveRestaurantFileUrl(url) {
  if (!url) return "";
  const value = String(url).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  if (value.startsWith("/")) return `${String(RESTAURANT_APP_BASE_URL).replace(/\/$/, "")}${value}`;
  return value;
}

function toObject(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
}

function hasPayloadData(value) {
  const obj = toObject(value, null);
  if (!obj || typeof obj !== "object") return false;
  if (Array.isArray(obj)) return obj.length > 0;
  return Object.values(obj).some((v) => {
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
  });
}

function firstPayload(candidates = []) {
  for (const candidate of candidates) {
    if (hasPayloadData(candidate)) return toObject(candidate, {});
  }
  return {};
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return value.split(",").map((x) => x.trim()).filter(Boolean);
  }
  return [value];
}

function textOf(value, fallback = "-") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return fallback;
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

function dateText(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function firstOf(obj, keys, fallback = undefined) {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
  }
  return fallback;
}

function requestTypeOf(row) {
  return String(row?.requestType || row?.request_type || row?.type || "CREATE").toUpperCase();
}

function payloadOf(row) {
  const base = unwrap(row, row);

  // The restaurant-app API stores real request data in request_payload.
  // Some old DB hotfixes also returned payload: {}, and {} is truthy in JS.
  // So never blindly pick payload first; pick the first object that actually has data.
  return firstPayload([
    base?.requestPayload,
    base?.request_payload,
    base?.payload,
    base?.data?.requestPayload,
    base?.data?.request_payload,
    base?.data?.payload,
  ]);
}

function oldPayloadOf(row) {
  const base = unwrap(row, row);
  return firstPayload([
    base?.currentSnapshot,
    base?.current_snapshot,
    base?.oldPayload,
    base?.old_payload,
    base?.previousPayload,
    base?.previous_payload,
  ]);
}

function foodInfo(payload = {}) {
  const basic = toObject(payload.basicInformation || payload.basic_information || payload.basic || payload.food || payload.item || {}, {});
  const merged = { ...payload, ...basic };
  return {
    name: firstOf(merged, ["name", "foodName", "food_name", "title", "itemName"], "Food item"),
    sku: firstOf(merged, ["sku", "code", "foodSku", "food_sku"], "-"),
    category: firstOf(merged, ["categoryName", "category_name", "category", "categoryTitle"], "-"),
    subcategory: firstOf(merged, ["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], "-"),
    foodType: firstOf(merged, ["foodType", "food_type", "dietType", "diet_type"], "-"),
    taxPercent: firstOf(merged, ["taxPercent", "tax_percent", "gst", "gstPercent"], "-"),
    description: firstOf(merged, ["description", "shortDescription", "short_description"], "-"),
    isActive: firstOf(merged, ["isActive", "is_active"], undefined),
  };
}

function variantsOf(payload = {}) {
  return toArray(payload.variants || payload.variantSystem || payload.variant_system || payload.foodVariants || payload.food_variants);
}

function tagsOf(payload = {}) {
  return toArray(payload.tags || payload.tagNames || payload.tag_names || payload.cuisineTags || payload.cuisine_tags);
}

function restaurantsOf(payload = {}) {
  return toArray(payload.restaurantAssignments || payload.restaurant_assignments || payload.restaurants || payload.restaurantIds || payload.restaurant_ids || payload.assignments);
}

function imagesOf(value) {
  const list = toArray(value);
  return list.map((item) => (typeof item === "string" ? { url: item } : toObject(item, {}))).filter((x) => x.url || x.imageUrl || x.image_url || x.preview);
}

function foodImagesOf(payload = {}) {
  const topLevel = imagesOf(payload.images || payload.foodImages || payload.food_images || payload.imageUrls || payload.image_urls || payload.imageUrl || payload.image_url);
  if (topLevel.length) return topLevel;

  // In restaurant requests, images usually live inside variants, not on the food root.
  return variantsOf(payload).flatMap((variant) => {
    const v = typeof variant === "object" ? variant : {};
    return imagesOf(v.images || v.variantImages || v.variant_images || v.imageUrl || v.image_url);
  });
}

function comboItemsOf(payload = {}) {
  return toArray(payload.items || payload.comboItems || payload.combo_items);
}

function isComboRequest(row = {}) {
  const type = requestTypeOf(row);
  return type.includes('COMBO') || row.requestKind === 'COMBO' || row.request_kind === 'COMBO';
}

function isDescriptionField(key = "") {
  const normalized = String(key).replace(/[_\s-]/g, "").toLowerCase();
  return normalized === "description" || normalized === "shortdescription";
}

function deepClone(value) {
  try {
    return JSON.parse(JSON.stringify(value || {}));
  } catch (_) {
    return Array.isArray(value) ? [...value] : { ...(value || {}) };
  }
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function valueByKeys(obj, keys = [], fallback = "") {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
}

function setByKnownKey(obj, keys = [], value, fallbackKey) {
  const targetKey = keys.find((key) => hasOwn(obj, key)) || fallbackKey || keys[0];
  if (targetKey) obj[targetKey] = value;
}

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function tagsText(value = {}) {
  return tagsOf(value)
    .map((tag) => (typeof tag === "object" ? firstOf(tag, ["name", "label", "title", "tag"], "") : tag))
    .filter(Boolean)
    .join(", ");
}

function makeEditDraft(row) {
  const draft = deepClone(payloadOf(row));
  draft.variants = variantsOf(draft).map((variant) => (typeof variant === "object" ? deepClone(variant) : { variantName: String(variant) }));
  draft.items = comboItemsOf(draft).map((item) => (typeof item === "object" ? deepClone(item) : { name: String(item), qty: 1 }));
  draft.__tagText = tagsText(draft);
  return draft;
}

const numericEditKeys = new Set([
  "price",
  "basePrice",
  "base_price",
  "mrp",
  "MRP",
  "sellingPrice",
  "selling_price",
  "restaurantPrice",
  "restaurant_price",
  "comboPrice",
  "combo_price",
  "taxPercent",
  "tax_percent",
  "preparationTimeMins",
  "preparation_time_mins",
  "prepTime",
  "prep_time",
  "qty",
  "quantity",
  "sortOrder",
  "sort_order",
]);

function normalizeEditedValue(key, value) {
  if (numericEditKeys.has(key)) {
    if (value === "" || value === undefined || value === null) return "";
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

function stripUiKeys(value) {
  if (Array.isArray(value)) return value.map(stripUiKeys);
  if (!value || typeof value !== "object") return value;
  const out = {};
  Object.entries(value).forEach(([key, item]) => {
    if (key.startsWith("__")) return;
    out[key] = normalizeEditedValue(key, stripUiKeys(item));
  });
  return out;
}

function buildApprovalPatch(draft = {}, comboRequest = false) {
  const patch = stripUiKeys(deepClone(draft));
  if (!comboRequest) {
    const tagNames = splitCsv(draft.__tagText);
    if (tagNames.length || hasOwn(patch, "tagNames") || hasOwn(patch, "tag_names") || hasOwn(patch, "tags")) {
      patch.tagNames = tagNames;
      delete patch.tag_names;
      delete patch.tags;
    }

    const rootImages = foodImageStringsOfDraft(patch);
    if (rootImages.length) {
      patch.images = rootImages;
      patch.imageUrl = rootImages.includes(patch.imageUrl) ? patch.imageUrl : rootImages[0];
      delete patch.image_url;
      delete patch.imageUrls;
      delete patch.image_urls;
      delete patch.foodImages;
      delete patch.food_images;
    }

    patch.variants = variantsOf(patch).map((variant, index) => {
      const normalized = typeof variant === "object" ? { ...variant } : { variantName: String(variant) };
      const price = firstOf(normalized, ["sellingPrice", "selling_price", "price", "restaurantPrice", "restaurant_price"], "");
      if (price !== "") {
        normalized.sellingPrice = price;
        normalized.price = price;
        if (!hasOwn(normalized, "restaurantPrice") && !hasOwn(normalized, "restaurant_price")) normalized.restaurantPrice = price;
      }
      const mrp = firstOf(normalized, ["mrp", "MRP"], "");
      if (mrp === "" && price !== "") normalized.mrp = price;
      const variantImages = variantImageStringsOf(normalized);
      if (variantImages.length) {
        normalized.images = variantImages;
        normalized.imageUrl = variantImages.includes(normalized.imageUrl) ? normalized.imageUrl : variantImages[0];
        delete normalized.image_url;
        delete normalized.variantImages;
        delete normalized.variant_images;
        delete normalized.imageUrls;
        delete normalized.image_urls;
      }
      return {
        ...normalized,
        sortOrder: valueByKeys(normalized, ["sortOrder", "sort_order"], index),
      };
    });
  } else {
    const comboPrice = firstOf(patch, ["comboPrice", "combo_price", "price"], "");
    if (comboPrice !== "") {
      patch.comboPrice = comboPrice;
      patch.price = comboPrice;
    }
    patch.items = comboItemsOf(patch).map((item) => (typeof item === "object" ? item : { name: String(item), qty: 1 }));
  }
  return patch;
}

function validationMessageForPatch(patch = {}, comboRequest = false) {
  if (comboRequest) {
    const name = String(firstOf(patch, ["name", "comboName", "combo_name"], "")).trim();
    if (!name) return "Combo name is required before approval.";
    const price = Number(firstOf(patch, ["comboPrice", "combo_price", "price"], 0));
    if (!Number.isFinite(price) || price <= 0) return "Combo price must be greater than 0.";
    const items = comboItemsOf(patch);
    if (items.length < 2) return "Combo must have at least 2 items.";
    return "";
  }

  const name = String(firstOf(patch, ["name", "foodName", "food_name", "title", "itemName"], "")).trim();
  if (!name) return "Food name is required before approval.";
  const variants = variantsOf(patch);
  if (!variants.length) return "At least one variant is required before approval.";
  for (let index = 0; index < variants.length; index += 1) {
    const variant = variants[index] || {};
    const variantName = String(firstOf(variant, ["variantName", "variant_name", "name", "title"], "")).trim();
    if (!variantName) return `Variant ${index + 1} name is required.`;
    const price = Number(firstOf(variant, ["sellingPrice", "selling_price", "price", "restaurantPrice", "restaurant_price"], 0));
    if (!Number.isFinite(price) || price <= 0) return `${variantName} price must be greater than 0.`;
  }
  return "";
}

function fieldValue(row, keys, fallback = "") {
  const value = valueByKeys(row, keys, fallback);
  return value === undefined || value === null ? "" : value;
}

function foodTypeSelectValue(value) {
  const normalized = String(value || "VEG").trim().toUpperCase().replace(/[-\s]+/g, "_");
  if (normalized === "NONVEG") return "NON_VEG";
  if (["VEG", "NON_VEG", "EGG"].includes(normalized)) return normalized;
  return "VEG";
}

const FOOD_TYPES = ["VEG", "NON_VEG", "EGG"];
const TAX_OPTIONS = ["0", "5", "12", "18", "28"];
const GROUP_OPTIONS = ["size", "plate", "flavor", "pack", "weight", "volume", "combo", "type", "spice", "base", "crust"];
const PACK_LABEL_OPTIONS = ["Plate", "Half Plate", "Full Plate", "Piece", "Pack", "Combo", "Small", "Medium", "Large", "Regular", "Family Pack", "Bottle", "Can"];
const UNIT_OPTIONS = ["pcs", "plate", "pack", "box", "bottle", "can", "g", "kg", "ml", "L"];
const WEIGHT_OPTIONS = ["g", "kg", "ml", "L"];
const MAX_APPROVAL_IMAGES = 3;
const MAX_APPROVAL_IMAGE_SIZE_MB = 3;
const MAX_APPROVAL_IMAGE_SIZE_BYTES = MAX_APPROVAL_IMAGE_SIZE_MB * 1024 * 1024;

function rowId(row = {}) {
  return row?.id || row?._id || row?.categoryId || row?.category_id || row?.subcategoryId || row?.subcategory_id || row?.tagId || row?.tag_id || "";
}

function rowName(row = {}, fallback = "") {
  return row?.name || row?.title || row?.label || row?.categoryName || row?.category_name || row?.subcategoryName || row?.subcategory_name || fallback;
}

function singleOf(res, fallback = {}) {
  const data = res?.data ?? res;
  if (!data) return fallback;
  if (Array.isArray(data)) return data[0] || fallback;
  if (data.row) return data.row;
  if (data.category) return data.category;
  if (data.subcategory) return data.subcategory;
  if (data.tag) return data.tag;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) return data.data;
  return data;
}

function imageUrlFromItem(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.url || item.imageUrl || item.image_url || item.preview || "";
}

function imageStringsOf(value) {
  return imagesOf(value)
    .map(imageUrlFromItem)
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .slice(0, MAX_APPROVAL_IMAGES);
}

function foodImageStringsOfDraft(draft = {}) {
  const direct = imageStringsOf(draft.images || draft.foodImages || draft.food_images || draft.imageUrls || draft.image_urls);
  const cover = String(draft.imageUrl || draft.image_url || "").trim();
  const merged = [...direct];
  if (cover && !merged.includes(cover)) merged.unshift(cover);
  return merged.filter(Boolean).slice(0, MAX_APPROVAL_IMAGES);
}

function variantImageStringsOf(variant = {}) {
  const direct = imageStringsOf(variant.images || variant.variantImages || variant.variant_images || variant.imageUrls || variant.image_urls);
  const cover = String(variant.imageUrl || variant.image_url || "").trim();
  const merged = [...direct];
  if (cover && !merged.includes(cover)) merged.unshift(cover);
  return merged.filter(Boolean).slice(0, MAX_APPROVAL_IMAGES);
}

function categoryIdOfSubcategory(row = {}) {
  return row?.categoryId || row?.category_id || row?.parentCategoryId || row?.parent_category_id || "";
}

async function uploadApprovalImage(file) {
  if (!file) return "";
  if (!String(file.type || "").startsWith("image/")) throw new Error("Only image files are allowed");
  if (file.size > MAX_APPROVAL_IMAGE_SIZE_BYTES) throw new Error(`Each image must be ${MAX_APPROVAL_IMAGE_SIZE_MB} MB or less`);
  const res = await restaurantAdminService.uploadFoodImage(file);
  const url = res?.data?.imageUrl || res?.data?.url || res?.imageUrl || res?.url;
  if (!url) throw new Error("Upload failed: image URL missing");
  return url;
}

function ApprovalImageEditor({ label, images = [], coverImage = "", onChange, onCoverChange, onError }) {
  const inputId = React.useId();
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const safeImages = imageStringsOf(images);

  async function handleFiles(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      if (files.length + safeImages.length > MAX_APPROVAL_IMAGES) {
        throw new Error(`Maximum ${MAX_APPROVAL_IMAGES} image(s) allowed`);
      }
      setUploading(true);
      const uploaded = [];
      for (const file of files) uploaded.push(await uploadApprovalImage(file));
      const next = [...safeImages, ...uploaded].filter(Boolean).slice(0, MAX_APPROVAL_IMAGES);
      onChange?.(next);
      if (!coverImage && next[0]) onCoverChange?.(next[0]);
    } catch (error) {
      onError?.(error?.message || "Image upload failed");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  function removeAt(index) {
    const removed = safeImages[index];
    const next = safeImages.filter((_, idx) => idx !== index);
    onChange?.(next);
    if (String(coverImage || "") === String(removed)) onCoverChange?.(next[0] || "");
  }

  return (
    <div className="rounded-[16px] border border-dashed border-[#f2c6cc] bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[13px] font-bold text-[#d86a78]">{label}</div>
          <div className="text-[11px] font-medium text-slate-500">Click image to preview • Delete/replace here before approval • {safeImages.length}/{MAX_APPROVAL_IMAGES}</div>
        </div>
        <label htmlFor={inputId} className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-bold text-[#d86a78] hover:bg-[#fff6f7]">
          <UploadCloud size={14} /> {uploading ? "Uploading..." : "Add image"}
        </label>
        <input id={inputId} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
      </div>

      {safeImages.length ? (
        <div className="flex flex-wrap gap-3">
          {safeImages.map((raw, idx) => {
            const src = resolveFoodImageUrl(raw);
            const isCover = String(coverImage || safeImages[0] || "") === String(raw);
            return (
              <div key={`${raw}-${idx}`} className="w-[126px] rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-2">
                <button type="button" onClick={() => setPreview(src || raw)} className="relative h-[88px] w-full overflow-hidden rounded-[10px] bg-slate-100 text-slate-300">
                  {src ? <img src={src} alt={`${label} ${idx + 1}`} className="h-full w-full object-cover" /> : <ImageIcon size={18} />}
                  <span className="absolute bottom-1 right-1 rounded-full bg-white/90 p-1 text-[#d86a78] shadow"><Eye size={12} /></span>
                </button>
                <div className="mt-2 flex gap-1">
                  {onCoverChange ? (
                    <button type="button" onClick={() => onCoverChange(raw)} className={`min-w-0 flex-1 rounded-[8px] border px-2 py-1 text-[10px] font-bold ${isCover ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-[#f2c6cc] bg-white text-[#d86a78]"}`}>
                      {isCover ? "Cover" : "Set"}
                    </button>
                  ) : null}
                  <button type="button" onClick={() => removeAt(idx)} className="rounded-[8px] bg-rose-50 px-2 py-1 text-rose-600 hover:bg-rose-100" title="Delete image">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[12px] bg-[#fffafb] px-4 py-5 text-center text-[12px] font-bold text-slate-400">No image selected</div>
      )}

      {preview ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/75 p-4" onClick={() => setPreview("")}> 
          <div className="relative max-h-[92vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPreview("")} className="absolute -right-3 -top-3 rounded-full bg-white p-2 text-[#d86a78] shadow"><X size={18} /></button>
            <img src={preview} alt="Large preview" className="max-h-[92vh] max-w-[92vw] rounded-[18px] bg-white object-contain shadow-2xl" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ApprovalEditModal({ row, saving, onClose, onApprove }) {
  const comboRequest = isComboRequest(row);
  const [draft, setDraft] = useState(() => makeEditDraft(row));
  const [localError, setLocalError] = useState("");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [newTagName, setNewTagName] = useState("");

  useEffect(() => {
    setDraft(makeEditDraft(row));
    setLocalError("");
  }, [row]);

  useEffect(() => {
    let alive = true;
    async function loadMeta() {
      if (!row || comboRequest) return;
      setMetaLoading(true);
      try {
        const [catRes, subRes, tagRes] = await Promise.all([
          restaurantAdminService.listFoodCategories({ limit: 500 }),
          restaurantAdminService.listFoodSubcategories({ limit: 1000 }),
          restaurantAdminService.listFoodTags({ limit: 500 }),
        ]);
        if (!alive) return;
        setCategories(rowsOf(catRes));
        setSubcategories(rowsOf(subRes));
        setTags(rowsOf(tagRes));
      } catch (error) {
        if (alive) setLocalError(error?.response?.data?.message || error?.message || "Catalog options load failed");
      } finally {
        if (alive) setMetaLoading(false);
      }
    }
    loadMeta();
    return () => { alive = false; };
  }, [row, comboRequest]);

  const setRoot = (keys, value, fallbackKey) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      setByKnownKey(next, keys, value, fallbackKey);
      return next;
    });
  };

  const setRootMany = (patches = []) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      patches.forEach(([keys, value, fallbackKey]) => setByKnownKey(next, keys, value, fallbackKey));
      return next;
    });
  };

  const setVariant = (index, keys, value, fallbackKey) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      const rows = variantsOf(next);
      rows[index] = typeof rows[index] === "object" ? { ...rows[index] } : { variantName: String(rows[index] || "") };
      setByKnownKey(rows[index], keys, value, fallbackKey);
      next.variants = rows;
      return next;
    });
  };

  const setVariantMany = (index, patches = []) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      const rows = variantsOf(next);
      rows[index] = typeof rows[index] === "object" ? { ...rows[index] } : { variantName: String(rows[index] || "") };
      patches.forEach(([keys, value, fallbackKey]) => setByKnownKey(rows[index], keys, value, fallbackKey));
      next.variants = rows;
      return next;
    });
  };

  const addVariant = () => {
    setDraft((prev) => {
      const next = deepClone(prev);
      const rows = variantsOf(next);
      rows.push({
        variantName: `Variant ${rows.length + 1}`,
        variantSku: "",
        barcode: "",
        variantGroup: "size",
        variantValue: "Regular",
        packLabel: "Regular",
        qtyPackValue: "",
        qtyPackUnit: "pcs",
        weightValue: "",
        weightUnitSymbol: "g",
        sellingPrice: "",
        mrp: "",
        taxPercent: fieldValue(next, ["taxPercent", "tax_percent"], 5),
        foodType: fieldValue(next, ["foodType", "food_type"], "VEG"),
        images: [],
        imageUrl: "",
        preparationTimeMins: fieldValue(next, ["preparationTimeMins", "preparation_time_mins"], 20),
        isActive: true,
        isDefault: rows.length === 0,
        sortOrder: rows.length,
      });
      next.variants = rows;
      return next;
    });
  };

  const removeVariant = (index) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      const rows = variantsOf(next).filter((_, idx) => idx !== index);
      if (rows.length && !rows.some((variant) => Boolean(valueByKeys(variant, ["isDefault", "is_default"], false)))) {
        setByKnownKey(rows[0], ["isDefault", "is_default"], true, "isDefault");
      }
      next.variants = rows.map((variant, idx) => ({ ...(variant || {}), sortOrder: valueByKeys(variant, ["sortOrder", "sort_order"], idx) }));
      return next;
    });
  };

  const makeDefaultVariant = (index) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      next.variants = variantsOf(next).map((variant, idx) => {
        const row = typeof variant === "object" ? { ...variant } : { variantName: String(variant || "") };
        setByKnownKey(row, ["isDefault", "is_default"], idx === index, "isDefault");
        return row;
      });
      return next;
    });
  };

  const setComboItem = (index, keys, value, fallbackKey) => {
    setDraft((prev) => {
      const next = deepClone(prev);
      const rows = comboItemsOf(next);
      rows[index] = typeof rows[index] === "object" ? { ...rows[index] } : { name: String(rows[index] || ""), qty: 1 };
      setByKnownKey(rows[index], keys, value, fallbackKey);
      next.items = rows;
      return next;
    });
  };

  const selectedCategoryId = String(fieldValue(draft, ["categoryId", "category_id"], ""));
  const selectedSubcategoryId = String(fieldValue(draft, ["subcategoryId", "subcategory_id"], ""));
  const selectedCategoryName = String(fieldValue(draft, ["categoryName", "category_name", "category", "categoryTitle"], ""));
  const selectedSubcategoryName = String(fieldValue(draft, ["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], ""));
  const selectedTagNames = splitCsv(draft.__tagText || "").map((x) => x.toLowerCase());

  const categoryOptions = useMemo(() => {
    const options = categories.map((category) => ({ value: String(rowId(category)), label: rowName(category, "Category") })).filter((o) => o.value);
    if (selectedCategoryId && !options.some((o) => String(o.value) === selectedCategoryId)) {
      options.unshift({ value: selectedCategoryId, label: selectedCategoryName || "Current category" });
    }
    return options;
  }, [categories, selectedCategoryId, selectedCategoryName]);

  const subcategoryOptions = useMemo(() => {
    const filtered = subcategories.filter((subcategory) => !selectedCategoryId || String(categoryIdOfSubcategory(subcategory)) === selectedCategoryId);
    const options = filtered.map((subcategory) => ({ value: String(rowId(subcategory)), label: rowName(subcategory, "Subcategory") })).filter((o) => o.value);
    if (selectedSubcategoryId && !options.some((o) => String(o.value) === selectedSubcategoryId)) {
      options.unshift({ value: selectedSubcategoryId, label: selectedSubcategoryName || "Current subcategory" });
    }
    return options;
  }, [subcategories, selectedCategoryId, selectedSubcategoryId, selectedSubcategoryName]);

  if (!row) return null;

  async function createCategoryInline() {
    const name = String(newCategoryName || "").trim();
    if (!name) return setLocalError("Category name is required.");
    try {
      setMetaLoading(true);
      const created = singleOf(await restaurantAdminService.createFoodCategory({ name, isActive: true }));
      const category = { ...created, id: rowId(created) || created.id, name: rowName(created, name) || name };
      setCategories((prev) => [category, ...prev.filter((item) => String(rowId(item)) !== String(rowId(category)))]);
      setRootMany([
        [["categoryId", "category_id"], rowId(category), "categoryId"],
        [["categoryName", "category_name", "category", "categoryTitle"], rowName(category, name), "categoryName"],
        [["subcategoryId", "subcategory_id"], "", "subcategoryId"],
        [["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], "", "subcategoryName"],
      ]);
      setNewCategoryName("");
      setLocalError("");
    } catch (error) {
      setLocalError(error?.response?.data?.message || error?.message || "Category create failed");
    } finally {
      setMetaLoading(false);
    }
  }

  async function createSubcategoryInline() {
    const name = String(newSubcategoryName || "").trim();
    if (!name) return setLocalError("Subcategory name is required.");
    if (!selectedCategoryId) return setLocalError("Select or create category first, then create subcategory.");
    try {
      setMetaLoading(true);
      const created = singleOf(await restaurantAdminService.createFoodSubcategory({ name, categoryId: selectedCategoryId, category_id: selectedCategoryId, isActive: true }));
      const subcategory = { ...created, id: rowId(created) || created.id, name: rowName(created, name) || name, categoryId: selectedCategoryId, category_id: selectedCategoryId };
      setSubcategories((prev) => [subcategory, ...prev.filter((item) => String(rowId(item)) !== String(rowId(subcategory)))]);
      setRootMany([
        [["subcategoryId", "subcategory_id"], rowId(subcategory), "subcategoryId"],
        [["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], rowName(subcategory, name), "subcategoryName"],
      ]);
      setNewSubcategoryName("");
      setLocalError("");
    } catch (error) {
      setLocalError(error?.response?.data?.message || error?.message || "Subcategory create failed");
    } finally {
      setMetaLoading(false);
    }
  }

  async function createTagInline() {
    const name = String(newTagName || "").trim();
    if (!name) return setLocalError("Tag name is required.");
    try {
      setMetaLoading(true);
      const created = singleOf(await restaurantAdminService.createFoodTag({ name, tagType: "cuisine", isActive: true }));
      const tag = { ...created, id: rowId(created) || created.id, name: rowName(created, name) || name };
      setTags((prev) => [tag, ...prev.filter((item) => String(rowName(item, "")).toLowerCase() !== name.toLowerCase())]);
      const nextTags = Array.from(new Set([...splitCsv(draft.__tagText || ""), rowName(tag, name)])).join(", ");
      setDraft((prev) => ({ ...prev, __tagText: nextTags }));
      setNewTagName("");
      setLocalError("");
    } catch (error) {
      setLocalError(error?.response?.data?.message || error?.message || "Tag create failed");
    } finally {
      setMetaLoading(false);
    }
  }

  function toggleTag(name) {
    const clean = String(name || "").trim();
    if (!clean) return;
    const current = splitCsv(draft.__tagText || "");
    const exists = current.some((item) => item.toLowerCase() === clean.toLowerCase());
    const next = exists ? current.filter((item) => item.toLowerCase() !== clean.toLowerCase()) : [...current, clean];
    setDraft((prev) => ({ ...prev, __tagText: next.join(", ") }));
  }

  const submit = () => {
    const patch = buildApprovalPatch(draft, comboRequest);
    const message = validationMessageForPatch(patch, comboRequest);
    if (message) {
      setLocalError(message);
      return;
    }
    setLocalError("");
    onApprove(row, patch);
  };

  const variants = variantsOf(draft);
  const items = comboItemsOf(draft);
  const foodImages = foodImageStringsOfDraft(draft);
  const foodCover = String(fieldValue(draft, ["imageUrl", "image_url"], foodImages[0] || ""));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="flex max-h-[92vh] w-full max-w-[1240px] flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#f2c6cc] p-5">
          <div>
            <h2 className="text-[22px] font-semibold text-[#d86a78]">Edit details before approval</h2>
            <p className="mt-1 text-[13px] text-slate-500">Admin edited values will become live after approval. No raw category/subcategory ID fields are shown here.</p>
          </div>
          <button onClick={onClose} className="rounded-full border border-[#f2c6cc] p-2 text-[#d86a78]"><X size={18} /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-auto p-5">
          {localError ? <div className="rounded-[14px] border border-rose-200 bg-rose-50 p-3 text-[13px] font-bold text-rose-700">{localError}</div> : null}

          {comboRequest ? (
            <Card>
              <h3 className="mb-4 text-[16px] font-semibold text-[#d86a78]">Combo details</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <Input label="Combo name" value={fieldValue(draft, ["name", "comboName", "combo_name"])} onChange={(e) => setRoot(["name", "comboName", "combo_name"], e.target.value, "name")} />
                <Input label="Combo price" type="number" value={fieldValue(draft, ["comboPrice", "combo_price", "price"])} onChange={(e) => setRoot(["comboPrice", "combo_price", "price"], e.target.value, "comboPrice")} />
                <Input label="MRP" type="number" value={fieldValue(draft, ["mrp", "MRP", "computedMrp", "computed_mrp"])} onChange={(e) => setRoot(["mrp", "MRP", "computedMrp", "computed_mrp"], e.target.value, "mrp")} />
                <Select label="Active" value={String(fieldValue(draft, ["isActive", "is_active"], true))} onChange={(e) => setRoot(["isActive", "is_active"], e.target.value === "true", "isActive")}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
                <Textarea label="Description" className="md:col-span-2 xl:col-span-4" value={fieldValue(draft, ["description"])} onChange={(e) => setRoot(["description"], e.target.value, "description")} />
              </div>
            </Card>
          ) : (
            <>
              <Card>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-[#d86a78]">Food basic details</h3>
                    <p className="text-[12px] text-slate-500">if needed change before submitting</p>
                  </div>
                  {metaLoading ? <span className="rounded-full bg-[#fff0f2] px-3 py-1 text-[11px] font-bold text-[#d86a78]">Loading catalog...</span> : null}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Input label="Food name" value={fieldValue(draft, ["name", "foodName", "food_name", "title", "itemName"])} onChange={(e) => setRoot(["name", "foodName", "food_name", "title", "itemName"], e.target.value, "name")} />
                  <Input label="SKU" value={fieldValue(draft, ["sku", "code", "foodSku", "food_sku"])} onChange={(e) => setRoot(["sku", "code", "foodSku", "food_sku"], e.target.value, "sku")} />
                  <Input label="Barcode" value={fieldValue(draft, ["barcode"])} onChange={(e) => setRoot(["barcode"], e.target.value.replace(/\D/g, "").slice(0, 15), "barcode")} placeholder="Optional" />
                  <Select label="Status" value={String(fieldValue(draft, ["status"], "active"))} onChange={(e) => setRoot(["status"], e.target.value, "status")}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </Select>
                  <Select label="Food type" value={foodTypeSelectValue(fieldValue(draft, ["foodType", "food_type", "dietType", "diet_type"], "VEG"))} onChange={(e) => setRoot(["foodType", "food_type", "dietType", "diet_type"], e.target.value, "foodType")}>
                    {FOOD_TYPES.map((type) => <option key={type} value={type}>{type === "NON_VEG" ? "NON VEG" : type}</option>)}
                  </Select>
                  <Select label="Active" value={String(fieldValue(draft, ["isActive", "is_active"], true))} onChange={(e) => setRoot(["isActive", "is_active"], e.target.value === "true", "isActive")}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Select>
                  <Select label="Tax %" value={String(fieldValue(draft, ["taxPercent", "tax_percent", "gst", "gstPercent"], 5))} onChange={(e) => setRoot(["taxPercent", "tax_percent", "gst", "gstPercent"], e.target.value, "taxPercent")}>
                    {TAX_OPTIONS.map((tax) => <option key={tax} value={tax}>{tax}%</option>)}
                  </Select>
                  <Input label="Slug" value={fieldValue(draft, ["slug"], "")} onChange={(e) => setRoot(["slug"], e.target.value.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""), "slug")} />

                  <Select
                    label="Main category"
                    className="xl:col-span-2"
                    value={selectedCategoryId}
                    onChange={(e) => {
                      const selectedOption = categoryOptions.find((item) => String(item.value) === String(e.target.value));
                      setRootMany([
                        [["categoryId", "category_id"], e.target.value, "categoryId"],
                        [["categoryName", "category_name", "category", "categoryTitle"], selectedOption?.label || "", "categoryName"],
                        [["subcategoryId", "subcategory_id"], "", "subcategoryId"],
                        [["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], "", "subcategoryName"],
                      ]);
                    }}
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </Select>
                  <Select
                    label="Subcategory"
                    className="xl:col-span-2"
                    value={selectedSubcategoryId}
                    onChange={(e) => {
                      const selectedOption = subcategoryOptions.find((item) => String(item.value) === String(e.target.value));
                      setRootMany([
                        [["subcategoryId", "subcategory_id"], e.target.value, "subcategoryId"],
                        [["subcategoryName", "subcategory_name", "subCategory", "subcategory", "sub_category"], selectedOption?.label || "", "subcategoryName"],
                      ]);
                    }}
                  >
                    <option value="">Select subcategory</option>
                    {subcategoryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </Select>

                  <div className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3 xl:col-span-2">
                    <div className="mb-2 text-[12px] font-bold text-slate-500">Create category here</div>
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="New category name" />
                      <Button variant="ghost" onClick={createCategoryInline} disabled={metaLoading}><Plus size={14} /> Create</Button>
                    </div>
                  </div>
                  <div className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3 xl:col-span-2">
                    <div className="mb-2 text-[12px] font-bold text-slate-500">Create subcategory here</div>
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                      <Input value={newSubcategoryName} onChange={(e) => setNewSubcategoryName(e.target.value)} placeholder="New subcategory name" />
                      <Button variant="ghost" onClick={createSubcategoryInline} disabled={metaLoading}><Plus size={14} /> Create</Button>
                    </div>
                  </div>

                  <div className="xl:col-span-4">
                    <ApprovalImageEditor
                      label="Food images"
                      images={foodImages}
                      coverImage={foodCover}
                      onError={setLocalError}
                      onCoverChange={(src) => setRoot(["imageUrl", "image_url"], src, "imageUrl")}
                      onChange={(imgs) => setRootMany([
                        [["images", "imageUrls", "image_urls", "foodImages", "food_images"], imgs, "images"],
                        [["imageUrl", "image_url"], imgs.includes(foodCover) ? foodCover : imgs[0] || "", "imageUrl"],
                      ])}
                    />
                  </div>

                  <div className="xl:col-span-4 rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-[13px] font-bold text-[#d86a78]">Food tags</div>
                        <div className="text-[11px] text-slate-500">Click existing tags or create a new tag.</div>
                      </div>
                      <div className="grid min-w-[280px] grid-cols-[1fr_auto] gap-2">
                        <Input value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder="New tag name" />
                        <Button variant="ghost" onClick={createTagInline} disabled={metaLoading}><Plus size={14} /> Add</Button>
                      </div>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {tags.length ? tags.map((tag) => {
                        const name = rowName(tag, "");
                        if (!name) return null;
                        const active = selectedTagNames.includes(name.toLowerCase());
                        return (
                          <button key={rowId(tag) || name} type="button" onClick={() => toggleTag(name)} className={`rounded-full border px-3 py-1.5 text-[12px] font-bold ${active ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]" : "border-[#f2c6cc] bg-white text-slate-500 hover:bg-[#fff6f7]"}`}>
                            {name}
                          </button>
                        );
                      }) : <span className="text-[12px] font-bold text-slate-400">No saved tags loaded</span>}
                    </div>
                    <Input label="Selected tags comma separated" value={draft.__tagText || ""} onChange={(e) => setDraft((prev) => ({ ...prev, __tagText: e.target.value }))} placeholder="Biryani, spicy, lunch" />
                  </div>

                  <Input label="Short description" className="xl:col-span-2" value={fieldValue(draft, ["shortDescription", "short_description"], "")} onChange={(e) => setRoot(["shortDescription", "short_description"], e.target.value, "shortDescription")} />
                  <Textarea label="Description" className="md:col-span-2 xl:col-span-4" value={fieldValue(draft, ["description"])} onChange={(e) => setRoot(["description"], e.target.value, "description")} />
                </div>
              </Card>

              <Card>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-[#d86a78]">Variants</h3>
                    <p className="text-[12px] text-slate-500">Full variant edit like Add Food: pack, qty, weight, tax, default, active and images.</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={addVariant}><Plus size={14} /> Add variant</Button>
                </div>
                <div className="space-y-4">
                  {variants.map((variant, index) => {
                    const variantImages = variantImageStringsOf(variant);
                    const variantCover = String(fieldValue(variant, ["imageUrl", "image_url"], variantImages[0] || ""));
                    return (
                      <div key={index} className="rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div className="text-[13px] font-bold text-[#d86a78]">Variant {index + 1}</div>
                          <div className="flex flex-wrap gap-2">
                            <Button variant={String(fieldValue(variant, ["isDefault", "is_default"], index === 0)) === "true" ? "primary" : "ghost"} size="sm" onClick={() => makeDefaultVariant(index)}>
                              {String(fieldValue(variant, ["isDefault", "is_default"], index === 0)) === "true" ? "Default" : "Make default"}
                            </Button>
                            {variants.length > 1 ? <Button variant="danger" size="sm" onClick={() => removeVariant(index)}><Trash2 size={14} /> Remove</Button> : null}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <Input label="Variant name" value={fieldValue(variant, ["variantName", "variant_name", "name", "title"])} onChange={(e) => setVariant(index, ["variantName", "variant_name", "name", "title"], e.target.value, "variantName")} />
                          <Input label="Variant SKU" value={fieldValue(variant, ["variantSku", "variant_sku", "sku"])} onChange={(e) => setVariant(index, ["variantSku", "variant_sku", "sku"], e.target.value, "variantSku")} />
                          <Input label="Barcode" value={fieldValue(variant, ["barcode"], "")} onChange={(e) => setVariant(index, ["barcode"], e.target.value.replace(/\D/g, "").slice(0, 15), "barcode")} placeholder="Optional" />
                          <Select label="Variant group" value={fieldValue(variant, ["variantGroup", "variant_group", "group"], "size")} onChange={(e) => setVariant(index, ["variantGroup", "variant_group", "group"], e.target.value, "variantGroup")}>
                            {GROUP_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                          </Select>
                          <Input label="Variant value" value={fieldValue(variant, ["variantValue", "variant_value", "value"])} onChange={(e) => setVariant(index, ["variantValue", "variant_value", "value"], e.target.value, "variantValue")} />
                          <Select label="Pack label" value={fieldValue(variant, ["packLabel", "pack_label"], "Regular")} onChange={(e) => setVariant(index, ["packLabel", "pack_label"], e.target.value, "packLabel")}>
                            <option value="">Select pack label</option>
                            {PACK_LABEL_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                          </Select>
                          <Input label="Qty / pack value" type="number" value={fieldValue(variant, ["qtyPackValue", "qty_pack_value"], "")} onChange={(e) => setVariant(index, ["qtyPackValue", "qty_pack_value"], e.target.value, "qtyPackValue")} />
                          <Select label="Qty / pack unit" value={fieldValue(variant, ["qtyPackUnit", "qty_pack_unit"], "pcs")} onChange={(e) => setVariant(index, ["qtyPackUnit", "qty_pack_unit"], e.target.value, "qtyPackUnit")}>
                            {UNIT_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                          </Select>
                          <Input label="Weight value" type="number" value={fieldValue(variant, ["weightValue", "weight_value"], "")} onChange={(e) => setVariant(index, ["weightValue", "weight_value"], e.target.value, "weightValue")} />
                          <Select label="Weight unit" value={fieldValue(variant, ["weightUnitSymbol", "weight_unit_symbol"], "g")} onChange={(e) => setVariant(index, ["weightUnitSymbol", "weight_unit_symbol"], e.target.value, "weightUnitSymbol")}>
                            {WEIGHT_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                          </Select>
                          <Select label="Food type" value={foodTypeSelectValue(fieldValue(variant, ["foodType", "food_type", "dietType"], fieldValue(draft, ["foodType", "food_type"], "VEG")))} onChange={(e) => setVariant(index, ["foodType", "food_type", "dietType"], e.target.value, "foodType")}>
                            {FOOD_TYPES.map((type) => <option key={type} value={type}>{type === "NON_VEG" ? "NON VEG" : type}</option>)}
                          </Select>
                          <Select label="Tax %" value={String(fieldValue(variant, ["taxPercent", "tax_percent"], fieldValue(draft, ["taxPercent", "tax_percent"], 5)))} onChange={(e) => setVariant(index, ["taxPercent", "tax_percent"], e.target.value, "taxPercent")}>
                            {TAX_OPTIONS.map((tax) => <option key={tax} value={tax}>{tax}%</option>)}
                          </Select>
                          <Input label="MRP" type="number" value={fieldValue(variant, ["mrp", "MRP"])} onChange={(e) => setVariant(index, ["mrp", "MRP"], e.target.value, "mrp")} />
                          <Input label="Selling price" type="number" value={fieldValue(variant, ["sellingPrice", "selling_price", "price", "restaurantPrice", "restaurant_price"])} onChange={(e) => setVariant(index, ["sellingPrice", "selling_price", "price", "restaurantPrice", "restaurant_price"], e.target.value, "sellingPrice")} />
                          <Input label="Prep mins" type="number" value={fieldValue(variant, ["preparationTimeMins", "preparation_time_mins", "prepTime", "prep_time"], fieldValue(draft, ["preparationTimeMins", "preparation_time_mins"], 20))} onChange={(e) => setVariant(index, ["preparationTimeMins", "preparation_time_mins", "prepTime", "prep_time"], e.target.value, "preparationTimeMins")} />
                          <Select label="Active" value={String(fieldValue(variant, ["isActive", "is_active"], true))} onChange={(e) => setVariant(index, ["isActive", "is_active"], e.target.value === "true", "isActive")}>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </Select>
                          <div className="xl:col-span-4">
                            <ApprovalImageEditor
                              label={`Variant ${index + 1} images`}
                              images={variantImages}
                              coverImage={variantCover}
                              onError={setLocalError}
                              onCoverChange={(src) => setVariant(index, ["imageUrl", "image_url"], src, "imageUrl")}
                              onChange={(imgs) => setVariantMany(index, [
                                [["images", "variantImages", "variant_images", "imageUrls", "image_urls"], imgs, "images"],
                                [["imageUrl", "image_url"], imgs.includes(variantCover) ? variantCover : imgs[0] || "", "imageUrl"],
                              ])}
                            />
                          </div>
                          <Input label="Short description" className="xl:col-span-2" value={fieldValue(variant, ["shortDescription", "short_description"], "")} onChange={(e) => setVariant(index, ["shortDescription", "short_description"], e.target.value, "shortDescription")} />
                          <Textarea label="Description" className="md:col-span-2 xl:col-span-4" value={fieldValue(variant, ["description"], "")} onChange={(e) => setVariant(index, ["description"], e.target.value, "description")} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}

          {comboRequest ? (
            <Card>
              <h3 className="mb-4 text-[16px] font-semibold text-[#d86a78]">Combo items</h3>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 gap-3 rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] p-4 md:grid-cols-[1fr_1fr_120px]">
                    <InfoCard label="Food" value={firstOf(item, ["foodName", "food_name", "name", "title"], `Item ${index + 1}`)} />
                    <InfoCard label="Variant" value={firstOf(item, ["variantName", "variant_name", "variant", "variantValue"], "-")} />
                    <Input label="Qty" type="number" value={fieldValue(item, ["qty", "quantity"], 1)} onChange={(e) => setComboItem(index, ["qty", "quantity"], e.target.value, "qty")} />
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-[#f2c6cc] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-[12px] font-semibold text-amber-700">Check carefully. Approval will directly make this live.</div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}><CheckCircle2 size={15} /> {saving ? "Approving..." : "Approve with edited details"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value, icon, className = "" }) {
  return (
    <div className={`min-w-0 rounded-[16px] border border-[#f2c6cc] p-4 ${className}`}>
      <div className="mb-2 flex items-center gap-2 text-[11px]  font-semibold uppercase tracking-wide text-slate-400">
        {icon || null}
        {label}
      </div>
      <div className="break-words text-[14px] font-semmibold text-slate-800">{textOf(value)}</div>
    </div>
  );
}

function looksLikeUuid(value = "") {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function ChipList({ items = [], empty = "No data" }) {
  const arr = toArray(items);
  if (!arr.length) return <span className="text-[13px] text-slate-400">{empty}</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {arr.map((item, idx) => {
        const rawLabel = typeof item === "object" ? firstOf(item, ["name", "label", "title", "tag"], "") : item;
        const label = rawLabel && !looksLikeUuid(rawLabel) ? rawLabel : `Item ${idx + 1}`;
        return <span key={`${label}-${idx}`} className="rounded-full bg-[#fff0f2] px-3 py-1 text-[11px]  font-semibold text-[#d86a78]">{label}</span>;
      })}
    </div>
  );
}

function ImageGrid({ images = [], title = "Images", gridClassName = "grid-cols-2 md:grid-cols-3" }) {
  const arr = imagesOf(images);
  if (!arr.length) return <div className="rounded-[14px] border border-dashed border-[#f2c6cc] bg-white p-5 text-center text-[12px] font-bold text-slate-400">No images uploaded</div>;
  return (
    <div className={`grid gap-3 ${gridClassName}`}>
      {arr.slice(0, 12).map((img, idx) => {
        const rawSrc = img.url || img.imageUrl || img.image_url || img.preview;
        const src = resolveFoodImageUrl(rawSrc);
        return (
          <div key={`${src}-${idx}`} className="overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-white shadow-sm">
            <div className="aspect-[4/3] bg-slate-100">
              {src ? <img src={src} alt={img.originalName || img.name || `${title} ${idx + 1}`} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon size={20} /></div>}
            </div>
            <div className="truncate px-2 py-1.5 text-[10px] font-bold text-slate-500">{img.originalName || img.name || `${title} ${idx + 1}`}</div>
          </div>
        );
      })}
    </div>
  );
}

function VariantsTable({ variants = [] }) {
  const rows = variantsOf({ variants });
  if (!rows.length) return <EmptyState title="No variants" subtitle="This request has no variant system data." icon={<Layers3 size={22} />} />;
  return (
    <TableShell columns={["Variant", "Type", "Group/Value", "Price", "Prep", "Images"]} minWidth={920}>
      {rows.map((variant, idx) => {
        const v = typeof variant === "object" ? variant : { name: variant };
        const name = firstOf(v, ["name", "variantName", "variant_name", "title"], `Variant ${idx + 1}`);
        const sku = firstOf(v, ["sku", "variantSku", "variant_sku"], "-");
        const group = firstOf(v, ["variantGroup", "variant_group", "group"], "-");
        const value = firstOf(v, ["variantValue", "variant_value", "value"], "-");
        const foodType = firstOf(v, ["foodType", "food_type", "dietType"], "-");
        const price = firstOf(v, ["sellingPrice", "selling_price", "price", "restaurantPrice", "restaurant_price", "basePrice", "base_price", "mrp", "MRP"], undefined);
        const mrp = firstOf(v, ["mrp", "MRP"], undefined);
        const prep = firstOf(v, ["preparationTimeMins", "preparation_time_mins", "prepTime", "prep_time"], "-");
        const images = imagesOf(v.images || v.variantImages || v.variant_images || v.imageUrl || v.image_url);
        const thumb = resolveFoodImageUrl(images?.[0]?.url || images?.[0]?.imageUrl || images?.[0]?.image_url || "");
        return (
          <tr key={`${name}-${idx}`} className="border-b last:border-0 align-top">
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-[#f2c6cc] bg-slate-100">
                  {thumb ? <img src={thumb} alt={name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon size={16} /></div>}
                </div>
                <div>
                  <div className=" font-semibold text-[#d86a78]">{name}</div>
                  <div className="text-[11px] text-slate-400">SKU: {sku}</div>
                </div>
              </div>
            </td>
            <td className="px-4 py-3"><span className="rounded-full bg-[#fff0f2] px-2 py-1 text-[11px]  font-semibold text-[#d86a78]">{foodType}</span></td>
            <td className="px-4 py-3"><div className="font-bold text-slate-700">{group}</div><div className="text-[12px] text-slate-500">{value}</div></td>
            <td className="px-4 py-3"><div className=" font-semibold text-[#d86a78]">{money(price)}</div>{mrp !== undefined ? <div className="text-[11px] text-slate-400">MRP {money(mrp)}</div> : null}</td>
            <td className="px-4 py-3"><span className="text-[12px] font-bold text-slate-600">{prep === "-" ? "-" : `${prep} mins`}</span></td>
            <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600">{images.length} image(s)</span></td>
          </tr>
        );
      })}
    </TableShell>
  );
}

function prettyLabel(key = "") {
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function isTechnicalIdKey(key = "") {
  const normalized = String(key || "").replace(/[.\s-]/g, "_").toLowerCase();
  if (normalized === "id") return true;
  if (normalized.endsWith("_id")) return true;
  if (normalized.endsWith("id") && /(^|_)(category|subcategory|restaurant|request|food|variant|employee|created_by|updated_by)/.test(normalized)) return true;
  return false;
}

function normalizeCompareValue(value) {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (value.startsWith("data:image")) return "[image uploaded]";
    return value;
  }
  if (Array.isArray(value)) return value.length ? JSON.stringify(value.map((x) => (typeof x === "object" ? toObject(x, {}) : x))) : "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function flattenForCompare(obj = {}, prefix = "") {
  const out = {};
  const source = toObject(obj, {});
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    const path = prefix ? `${prefix}.${key}` : key;
    if (isTechnicalIdKey(path)) return;
    if (key.toLowerCase().includes("image") || key.toLowerCase().includes("url")) return;
    if (Array.isArray(value)) {
      out[path] = value.length ? `${value.length} item(s)` : "";
      return;
    }
    if (typeof value === "object") {
      const nested = flattenForCompare(value, path);
      Object.assign(out, nested);
      return;
    }
    out[path] = normalizeCompareValue(value);
  });
  return out;
}

function changeRowsOf(oldPayload = {}, payload = {}, reqType = "") {
  const requested = flattenForCompare(payload);
  const current = flattenForCompare(oldPayload);
  const keys = Array.from(new Set([...Object.keys(requested), ...Object.keys(current)]));
  const isNew = String(reqType || "").toUpperCase().includes("NEW") || !Object.keys(current).length;
  return keys
    .map((key) => {
      const before = current[key] || "";
      const after = requested[key] || "";
      const changed = isNew ? Boolean(after) : before !== after;
      return { key, before, after, changed, isNew };
    })
    .filter((row) => row.changed && row.after)
    .slice(0, 18);
}

function ChangeHighlights({ oldPayload = {}, payload = {}, reqType = "" }) {
  const rows = changeRowsOf(oldPayload, payload, reqType);
  if (!rows.length) return null;
  const isNew = String(reqType || "").toUpperCase().includes("NEW") || !Object.keys(toObject(oldPayload, {})).length;
  return (
    <Card className="border-[#ffd6a6]">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-[16px]  font-semibold text-slate-800"> {isNew ? "New request highlights" : "Changed fields"}</h3>
          <p className="mt-1 text-[12px] font-medium text-slate-500">Yellow cards show what restaurant is asking admin to make live.</p>
        </div>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px]  font-semibold text-amber-700">{rows.length} highlight(s)</span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <div key={row.key} className="rounded-[16px] border border-amber-200 bg-white p-3 shadow-sm">
            <div className="mb-2 text-[11px]  font-semibold uppercase tracking-wide text-slate-400">{prettyLabel(row.key)}</div>
            {!isNew ? <div className="mb-2 rounded-lg px-3 py-2 text-[12px] font-bold text-slate-500">Old: {row.before || "-"}</div> : null}
            <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-[12px]  font-semibold text-slate-800">
              {/* {!isNew ? <ArrowRight size={14} className="text-amber-600" /> : <BadgeCheck size={14} className="text-amber-600" />} */}
              <span className="break-words">{isNew ? "New: " : "Requested: "}{row.after}</span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ComboItemsTable({ items = [] }) {
  const rows = comboItemsOf({ items });
  if (!rows.length) return <EmptyState title="No combo items" subtitle="This combo request has no selected food items." icon={<Layers3 size={22} />} />;
  return (
    <TableShell columns={["Food", "Variant", "Qty", "Price"]} minWidth={720}>
      {rows.map((item, idx) => {
        const row = typeof item === "object" ? item : { name: item };
        const foodName = firstOf(row, ["foodName", "food_name", "name", "title"], `Food ${idx + 1}`);
        const variantName = firstOf(row, ["variantName", "variant_name", "variant", "variantValue"], "-");
        const qty = firstOf(row, ["qty", "quantity"], 1);
        const price = firstOf(row, ["price", "sellingPrice", "selling_price", "restaurantPrice"], undefined);
        return (
          <tr key={`${foodName}-${idx}`} className="border-b last:border-0 align-top">
            <td className="px-4 py-3  font-semibold text-[#d86a78]">{foodName}</td>
            <td className="px-4 py-3 text-[13px] font-bold text-slate-600">{variantName}</td>
            <td className="px-4 py-3"><span className="rounded-full bg-[#fff0f2] px-2 py-1 text-[11px]  font-semibold text-[#d86a78]">x{qty}</span></td>
            <td className="px-4 py-3  font-semibold text-[#d86a78]">{price !== undefined ? money(price) : "-"}</td>
          </tr>
        );
      })}
    </TableShell>
  );
}

function KeyValueGrid({ title, data = {}, skipKeys = [] }) {
  const obj = toObject(data, {});
  const entries = Object.entries(obj)
    .filter(([key, value]) => !skipKeys.includes(key) && !isTechnicalIdKey(key) && value !== undefined && value !== null && value !== "" && typeof value !== "object")
    .sort(([a], [b]) => Number(isDescriptionField(a)) - Number(isDescriptionField(b)));
  if (!entries.length) return null;
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4">
      <h3 className="mb-3 text-[14px]  font-semibold text-[#d86a78]">{title}</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {entries.map(([key, value]) => <InfoCard key={key} label={key.replace(/_/g, " ")} value={textOf(value)} className={isDescriptionField(key) ? "md:col-span-2" : ""} />)}
      </div>
    </div>
  );
}

function isProfileRequest(row = {}) {
  const kind = String(row?.requestKind || row?.request_kind || row?.kind || "").toUpperCase();
  const type = requestTypeOf(row);
  return kind === "PROFILE" || kind === "RESTAURANT_PROFILE" || type.includes("PROFILE");
}

function requestKeyOf(row = {}) {
  const kind = String(row?.requestKind || row?.request_kind || requestTypeOf(row) || "REQUEST").toUpperCase();
  return `${kind}:${idOf(row)}`;
}

function profilePayloadOf(row = {}) {
  const base = unwrap(row, row);
  return firstPayload([
    base?.requestPayload,
    base?.request_payload,
    base?.payload,
    base?.data?.requestPayload,
    base?.data?.request_payload,
    base?.data?.payload,
  ]);
}

function profileTitleOf(row = {}, payload = {}) {
  return (
    row?.restaurantName ||
    row?.restaurant_name ||
    payload?.restaurantName ||
    payload?.restaurant_name ||
    payload?.name ||
    "Restaurant profile"
  );
}

function profileSummaryOf(payload = {}) {
  const fields = Object.keys(toObject(payload, {})).filter((key) => {
    const value = payload[key];
    return !isTechnicalIdKey(key) && value !== undefined && value !== null && value !== "";
  });
  return fields.length ? `${fields.length} field(s) requested` : "Profile update request";
}

function profileDisplayValue(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : "-";
  if (typeof value === "object") return Object.keys(value).length ? `${Object.keys(value).length} item(s)` : "-";
  return String(value);
}

function profileDocumentEntries(documents = {}) {
  const docs = toObject(documents, {});
  return Object.entries(docs)
    .map(([key, value]) => {
      const obj = typeof value === "object" && value !== null ? value : { url: value };
      const url = obj.url || obj.path || obj.fileUrl || obj.file_url || obj.imageUrl || obj.image_url || obj.preview || "";
      return {
        key,
        label: prettyLabel(obj.label || obj.name || key),
        url: resolveRestaurantFileUrl(url),
        raw: value,
      };
    })
    .filter((item) => item.url || item.raw);
}

function ProfileDocumentsGrid({ documents = {} }) {
  const docs = profileDocumentEntries(documents);
  if (!docs.length) return null;
  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><ImageIcon size={17} className="text-[#d86a78]" /> Requested Documents / Images</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
        {docs.map((doc) => {
          const isImage = /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(doc.url) || String(doc.url).startsWith("data:image");
          return (
            <div key={doc.key} className="overflow-hidden rounded-[16px] border border-[#f2c6cc] bg-white">
              <div className="flex h-36 items-center justify-center bg-slate-50">
                {isImage && doc.url ? (
                  <img src={doc.url} alt={doc.label} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-center text-slate-400"><UploadCloud className="mx-auto mb-2" size={22} /><div className="text-[12px] font-semibold">Document</div></div>
                )}
              </div>
              <div className="p-3">
                <div className="truncate text-[13px] font-bold text-slate-700">{doc.label}</div>
                {doc.url ? <a className="mt-1 inline-flex text-[12px] font-bold text-[#d86a78] hover:underline" href={doc.url} target="_blank" rel="noreferrer">Open file</a> : <div className="mt-1 text-[12px] text-slate-500">File value present</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ProfileRequestDetails({ row, onApprove, onReject, approving }) {
  const payload = profilePayloadOf(row);
  const raw = unwrap(row, row);
  const pending = String(row.status || "").toUpperCase() === "PENDING";
  const docs = payload.documents || payload.restaurantDocuments || payload.restaurant_documents || {};
  const entries = Object.entries(payload)
    .filter(([key, value]) => key !== "documents" && key !== "restaurantDocuments" && key !== "restaurant_documents" && !isTechnicalIdKey(key) && value !== undefined && value !== null && value !== "")
    .sort(([a], [b]) => prettyLabel(a).localeCompare(prettyLabel(b)));

  return (
    <div className="space-y-5">
      <div className="rounded-[22px] border border-[#f2c6cc] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <StatusPill value={row.status} />
              <span className="rounded-full bg-[#fff6f7] px-3 py-1 text-[11px] font-bold text-[#d86a78]">RESTAURANT PROFILE</span>
            </div>
            <h2 className="text-[24px]  font-semibold text-slate-800">{profileTitleOf(row, payload)}</h2>
            <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-slate-500">Owner requested restaurant/business profile changes. These must not go live until admin approves.</p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold text-slate-500">
              <span className="rounded-full bg-white px-3 py-1">Restaurant ID: {raw.restaurantId || raw.restaurant_id || "-"}</span>
              <span className="rounded-full bg-white px-3 py-1">Created: {dateText(row.createdAt || row.created_at)}</span>
              {row.rejectionReason || row.rejection_reason ? <span className="rounded-full bg-rose-50 px-3 py-1 text-rose-700">Rejected: {row.rejectionReason || row.rejection_reason}</span> : null}
            </div>
          </div>
          {pending ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button variant="danger" disabled={approving} onClick={() => onReject(row)}><XCircle size={15} /> Reject</Button>
              <Button disabled={approving} onClick={() => onApprove(row)}><CheckCircle2 size={15} /> Approve</Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <InfoCard label="Restaurant" icon={<Store size={13} />} value={profileTitleOf(row, payload)} />
        <InfoCard label="Request Type" icon={<ClipboardCheck size={13} />} value="Restaurant profile" />
        <InfoCard label="Requested Fields" icon={<ListChecks size={13} />} value={profileSummaryOf(payload)} />
        <InfoCard label="Created" icon={<ClipboardCheck size={13} />} value={dateText(row.createdAt || row.created_at)} />
      </div>

      <Card>
        <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><Store size={17} className="text-[#d86a78]" /> Requested Restaurant Details</h3>
        {entries.length ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {entries.map(([key, value]) => (
              <InfoCard key={key} label={prettyLabel(key)} value={profileDisplayValue(value)} className={isDescriptionField(key) ? "md:col-span-2" : ""} />
            ))}
          </div>
        ) : (
          <EmptyState title="No editable fields found" subtitle="The request exists, but no valid restaurant profile fields were returned." icon={<Store size={22} />} />
        )}
      </Card>

      <ProfileDocumentsGrid documents={docs} />
    </div>
  );
}

function RequestDetails({ row, onApprove, onReject, onEdit, approving }) {
  if (!row) return <EmptyState title="Select a request" subtitle="Review restaurant product, combo and profile requests here." />;
  if (isProfileRequest(row)) {
    return <ProfileRequestDetails row={row} onApprove={onApprove} onReject={onReject} approving={approving} />;
  }
  const payload = payloadOf(row);
  const oldPayload = oldPayloadOf(row);
  const comboRequest = isComboRequest(row);
  const info = comboRequest
    ? { name: firstOf(payload, ['name', 'comboName', 'combo_name'], 'Combo request'), sku: '-', category: 'Combo', subcategory: '-', foodType: 'COMBO', taxPercent: '-', description: firstOf(payload, ['description'], '-'), isActive: undefined }
    : foodInfo(payload);
  const variants = variantsOf(payload);
  const comboItems = comboItemsOf(payload);
  const tags = tagsOf(payload);
  const assignments = restaurantsOf(payload);
  const foodImages = foodImagesOf(payload);
  const reqType = requestTypeOf(row);
  const pending = String(row.status || "").toUpperCase() === "PENDING";
  const raw = unwrap(row, row);

  return (
    <div className="space-y-5">
      <div className="rounded-[22px] border border-[#f2c6cc] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <StatusPill value={row.status} />
              {/* <span className="rounded-full bg-white px-3 py-1 text-[11px]  font-semibold text-[#d86a78]">{reqType}</span> */}
            </div>
            <h2 className="text-[24px]  font-semibold text-slate-800">{info.name}</h2>
            <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-slate-500">{info.description}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold text-slate-500">
              <span className="rounded-full bg-white px-3 py-1">Requested from: {raw.requestedFrom || raw.requested_from || payload.requestedFrom || payload.requested_from || "restaurant dashboard"}</span>
              {reqType.includes("NEW") ? <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">New request</span> : null}
            </div>
          </div>
          {pending ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button variant="ghost" disabled={approving} onClick={() => onEdit(row)}><Pencil size={15} /> Edit Details</Button>
              <Button variant="danger" disabled={approving} onClick={() => onReject(row)}><XCircle size={15} /> Reject</Button>
              <Button disabled={approving} onClick={() => onApprove(row)}><CheckCircle2 size={15} /> Approve</Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <InfoCard label="Restaurant" icon={<Store size={13} />} value={raw.restaurantName || raw.restaurant_name || "-"} />
        <InfoCard label="SKU" icon={<PackageCheck size={13} />} value={info.sku} />
        <InfoCard label="Food Type" icon={<ListChecks size={13} />} value={info.foodType} />
        <InfoCard label="Created" icon={<ClipboardCheck size={13} />} value={dateText(row.createdAt || row.created_at)} />
      </div>

      <ChangeHighlights oldPayload={oldPayload} payload={payload} reqType={reqType} />

      {comboRequest ? (
        <Card className="overflow-hidden">
          <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><Layers3 size={17} className="text-[#d86a78]" /> Combo Details</h3>
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[220px_minmax(0,1fr)]">
            <div className="min-w-0">
              <div className="mb-2 text-[12px]  font-semibold uppercase text-slate-400">Combo Image</div>
              <ImageGrid title="Combo" images={imagesOf(payload.imageUrl || payload.image_url || payload.images)} gridClassName="grid-cols-1" />
            </div>
            <div className="min-w-0">
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                <InfoCard label="Items" value={`${comboItems.length} item(s)`} />
                <InfoCard label="Items total" icon={<DollarSign size={13} />} value={money(firstOf(payload, ['computedSellingTotal', 'computed_selling_total', 'totalItemsPrice', 'total_items_price', 'totalMrp'], undefined))} />
                <InfoCard label="Combo price" icon={<DollarSign size={13} />} value={money(firstOf(payload, ['comboPrice', 'combo_price', 'price'], undefined))} />
                <InfoCard label="MRP" icon={<DollarSign size={13} />} value={money(firstOf(payload, ['mrp', 'MRP', 'computedMrp', 'computed_mrp'], undefined))} />
              </div>
              <ComboItemsTable items={comboItems} />
            </div>
          </div>
        </Card>
      ) : null}

      {!comboRequest ? <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><Tag size={17} className="text-[#d86a78]" /> Category Details</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InfoCard label="Category" value={info.category} />
            <InfoCard label="Subcategory" value={info.subcategory} />
            <InfoCard label="Tax" value={info.taxPercent === "-" ? "-" : `${info.taxPercent}%`} />
            <InfoCard label="Active" value={info.isActive === undefined ? "-" : info.isActive} />
          </div>
          <div className="mt-4">
            <div className="mb-2 text-[12px]  font-semibold uppercase text-slate-400">Tags</div>
            <ChipList items={tags} empty="No tags selected" />
          </div>
        </Card>
        <Card>
          <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><ImageIcon size={17} className="text-[#d86a78]" /> Food Images</h3>
          <ImageGrid images={foodImages} />
        </Card>
      </div> : null}

      {!comboRequest ? <Card>
        <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><Layers3 size={17} className="text-[#d86a78]" /> Variant System</h3>
        <VariantsTable variants={variants} />
      </Card> : null}

      {!comboRequest ? <Card>
        <h3 className="mb-3 flex items-center gap-2 text-[16px]  font-semibold text-[#d86a78]"><Store size={17} className="text-[#d86a78]" /> Restaurant Assignment</h3>
        <ChipList items={assignments} empty="No restaurant assignment found in request" />
      </Card> : null}

    {reqType.includes("EDIT") || Object.keys(oldPayload).length ? (
  <div className="max-h-[720px] grid grid-cols-1 gap-4 min-[1450px]:grid-cols-2">
    <KeyValueGrid
      title="Current / Old Values"
      data={oldPayload}
      skipKeys={[
        "variants",
        "images",
        "image",
        "imageUrl",
        "image_url",
        "imageUrls",
        "image_urls",
        "foodImages",
        "food_images",
        "variantImages",
        "variant_images",
        "restaurantAssignments",
        "restaurant_assignments",
        "tags",
        "tagNames",
        "tag_names",
      ]}
    />
    <KeyValueGrid
      title="Requested Values"
      data={payload}
      skipKeys={[
        "variants",
        "images",
        "image",
        "imageUrl",
        "image_url",
        "imageUrls",
        "image_urls",
        "foodImages",
        "food_images",
        "variantImages",
        "variant_images",
        "restaurantAssignments",
        "restaurant_assignments",
        "tags",
        "tagNames",
        "tag_names",
      ]}
    />
  </div>
) : null}
    </div>
  );
}

export default function FoodApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("PENDING");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [editing, setEditing] = useState(null);

  const counts = useMemo(() => {
    const pending = rows.filter((r) => String(r.status).toUpperCase() === "PENDING").length;
    const approved = rows.filter((r) => String(r.status).toUpperCase() === "APPROVED").length;
    const rejected = rows.filter((r) => String(r.status).toUpperCase() === "REJECTED").length;
    return { pending, approved, rejected, total: rows.length };
  }, [rows]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [foodRes, comboRes, profileRes] = await Promise.all([
        restaurantAdminService.listFoodRequests({ status, q, limit: 100 }),
        restaurantAdminService.listComboRequests({ status, q, limit: 100 }).catch(() => ({ data: { rows: [] } })),
        restaurantAdminService.listProfileRequests({ status, q, limit: 100 }).catch(() => ({ data: { rows: [] } })),
      ]);
      const list = [
        ...rowsOf(profileRes).map((item) => ({ ...item, requestKind: 'PROFILE', request_type: item.request_type || 'RESTAURANT_PROFILE_UPDATE' })),
        ...rowsOf(foodRes).map((item) => ({ ...item, requestKind: 'FOOD' })),
        ...rowsOf(comboRes).map((item) => ({ ...item, requestKind: 'COMBO', request_type: item.request_type || 'NEW_COMBO' })),
      ].sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
      setRows(list);
      setSelected((prev) => {
        const freshSelected = prev ? list.find((x) => requestKeyOf(x) === requestKeyOf(prev)) : null;
        return freshSelected || list[0] || null;
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function approve(row, adminPatch = null) {
    setSaving(true);
    setError("");
    try {
      const hasEditedPatch = adminPatch && Object.keys(adminPatch).length > 0;
      const approvalPayload = hasEditedPatch
        ? { ...adminPatch, note: "Approved by admin after editing request details" }
        : { note: "Approved by admin" };
      if (isProfileRequest(row)) await restaurantAdminService.approveProfileRequest(idOf(row), approvalPayload);
      else if (isComboRequest(row)) await restaurantAdminService.approveComboRequest(idOf(row), approvalPayload);
      else await restaurantAdminService.approveFoodRequest(idOf(row), approvalPayload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function reject(row, noteValue) {
    const note = String(noteValue || "").trim();
    if (!note) return setError("Reject reason is required");
    setSaving(true);
    setError("");
    try {
      if (isProfileRequest(row)) await restaurantAdminService.rejectProfileRequest(idOf(row), { reason: note, note });
      else if (isComboRequest(row)) await restaurantAdminService.rejectComboRequest(idOf(row), { reason: note, note });
      else await restaurantAdminService.rejectFoodRequest(idOf(row), { reason: note, note });
      setRejecting(null);
      setRejectNote("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingBox label="Loading approval requests..." />;

  return (
    <div>
      <PageHeader
        // eyebrow="Restaurant Catalog"
        icon={<ClipboardCheck size={14} />}
        title="Approval Requests"
        subtitle="Review restaurant profile, item and combo requests. Changes are applied only after approval."
        actions={<Button variant="ghost" onClick={load}><RefreshCw size={16} /> Refresh</Button>}
      />
      <ErrorBox error={error} onRetry={load} />

      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card><div className="text-[26px]  font-semibold text-[#d86a78]">{counts.pending}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Pending</div></Card>
        <Card><div className="text-[26px]  font-semibold text-emerald-600">{counts.approved}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Approved</div></Card>
        <Card><div className="text-[26px]  font-semibold text-rose-600">{counts.rejected}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Rejected</div></Card>
        <Card><div className="text-[26px]  font-semibold text-[#d86a78]">{counts.total}</div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Filtered</div></Card>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 rounded-[20px] border border-[#f2c6cc] bg-white p-4 md:grid-cols-[1fr_180px_auto] md:items-end">
        <Input label="Search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Restaurant, food/combo name or request id" />
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="PENDING">PENDING</option>
          <option value="APPROVED">APPROVED</option>
          <option value="REJECTED">REJECTED</option>
          <option value="">All</option>
        </Select>
        <Button onClick={load}><Search size={16} /> Apply</Button>
      </div>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[440px_1fr]">
        <Card className="flex max-h-[60vh] flex-col overflow-hidden xl:h-[720px] xl:max-h-none">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[20px]  font-semibold text-[#d86a78]">Requests</h2>
              <p className="text-[12px] text-slate-500">{rows.length} request(s)</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff0f2] text-[#d86a78]"><ClipboardCheck size={20} /></div>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-auto pr-1">
            {rows.length ? rows.map((r) => {
              const profileRequest = isProfileRequest(r);
              const payload = profileRequest ? profilePayloadOf(r) : payloadOf(r);
              const info = profileRequest
                ? { name: profileTitleOf(r, payload), category: 'Profile' }
                : isComboRequest(r) ? { name: firstOf(payload, ['name', 'comboName', 'combo_name'], 'Combo request'), category: 'Combo' } : foodInfo(payload);
              const active = requestKeyOf(selected) === requestKeyOf(r);
              return (
                <button
                  key={requestKeyOf(r)}
                  type="button"
                  onClick={() => setSelected(r)}
                  className={`w-full rounded-[18px] border p-4 text-left transition ${active ? "border-[#d86a78] shadow-sm" : "border-[#f2c6cc] bg-white hover:bg-[#fffafb]"}`}
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[14px]  font-semibold text-slate-800">{info.name}</div>
                      <div className="mt-0.5 truncate text-[12px] text-slate-500">{r.restaurantName || r.restaurant_name || r.restaurant_id || "Restaurant"}</div>
                    </div>
                    <StatusPill value={r.status} />
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px] font-bold text-slate-500">
                    <span className={`${requestTypeOf(r).includes("NEW") ? "bg-amber-100 text-amber-700" : profileRequest ? "bg-[#fff6f7] text-[#d86a78]" : "bg-slate-100"} rounded-full px-2 py-1`}>{requestTypeOf(r)}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-1">{profileRequest ? 'Restaurant Profile' : isComboRequest(r) ? 'Combo' : info.category}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-1">{profileRequest ? profileSummaryOf(payload) : isComboRequest(r) ? `${comboItemsOf(payload).length} items` : `${variantsOf(payload).length} variants`}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-1">{dateText(r.createdAt || r.created_at)}</span>
                  </div>
                </button>
              );
            }) : <EmptyState title="No approval requests" subtitle="Restaurant profile, add/edit product and combo requests will appear here." icon={<ClipboardCheck size={22} />} />}
          </div>
        </Card>

        <div className="min-w-0 xl:h-[720px] xl:overflow-auto xl:pr-1">
          <RequestDetails
            row={selected}
            approving={saving}
            onApprove={approve}
            onReject={(row) => { setRejecting(row); setRejectNote(""); }}
            onEdit={(row) => setEditing(row)}
          />
        </div>
      </div>

      {editing ? (
        <ApprovalEditModal
          row={editing}
          saving={saving}
          onClose={() => setEditing(null)}
          onApprove={approve}
        />
      ) : null}

      {rejecting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
          <div className="w-full max-w-[520px] rounded-[24px] bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[21px]  font-semibold text-[#d86a78]">Reject request</h2>
                <p className="text-[13px] text-slate-500">Give a clear reason. Restaurant will see this note.</p>
              </div>
              <button onClick={() => setRejecting(null)} className="rounded-full border border-[#f2c6cc] p-2 text-[#d86a78]"><X size={18} /></button>
            </div>
            <Textarea label="Reject reason" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Example: Variant image is missing / Price mismatch / Category is incorrect" />
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button variant="danger" disabled={saving} onClick={() => reject(rejecting, rejectNote)}>{saving ? "Rejecting..." : "Reject Request"}</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
